import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { s } from '../PeriodStatsPage.styles';
import { downloadAsExcel } from '../../../../utils/downloadAsExcel';
import { formatCurrency } from '../../../../utils/Function';
import { useDragScroll } from '../../../../utils/useDragScroll';
import BottomModal from '../../../components/modal/BottomModal';

type UsageAuctionOverviewProps = {
  periodSummary: string;
  region: string;
  isDetail: boolean;
  onToggleDetail: () => void;
  usage: string;
  usageDetail?: string;
  court?: string;
  fiMain?: string;
  fiSub?: string;
  usageData?: UsageRow[];
  loading?: boolean;
  error?: string | null;
};

export type UsageRow = {
  usage: string;
  claimAmount: number;
  bidRate: number;
  winPriceRate: number;
  collateralValueRate: number;
  recoveryRate: number;
  lossRate: number;
  mortgageAmount?: number;
  shortfallRate?: number;
};

// 화면용 정규화 타입
type UsageMetricsItem = {
  id: string;
  usage: string;
  claimAmount: number;
  bidRate: number; // %
  winPriceRate: number; // %
  collateralValueRate: number; // %
  recoveryRate: number; // %
  lossRate: number; // %
  mortgageAmount: number; // 원
  shortfallRate: number;
};

export default function NPLUsageOverview({
  periodSummary,
  region,
  isDetail,
  onToggleDetail,
  usage,
  usageDetail,
  court,
  fiMain,
  fiSub,
  usageData,
  loading,
  error,
}: UsageAuctionOverviewProps) {
  type SortKey =
    | 'claimAmount'
    | 'bidRate'
    | 'winPriceRate'
    | 'collateralValueRate'
    | 'recoveryRate'
    | 'lossRate';
  type SortDir = 'asc' | 'desc';

  const [sortKey, setSortKey] = useState<SortKey>('claimAmount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDragScroll();

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const toNum = (v: unknown) => {
    if (v == null) return 0;
    if (typeof v === 'number') return v;
    const n = Number(String(v).replace(/[,%]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  const source: UsageMetricsItem[] = useMemo(() => {
    const rows = Array.isArray(usageData) ? usageData : [];
    return rows.map((r, idx) => ({
      id: `${idx}-${r.usage ?? ''}`,
      usage: r.usage ?? '',
      claimAmount: toNum(r.claimAmount),
      bidRate: toNum(r.bidRate),
      winPriceRate: toNum(r.winPriceRate),
      collateralValueRate: toNum(r.collateralValueRate),
      recoveryRate: toNum(r.recoveryRate),
      lossRate: toNum(r.lossRate),
      mortgageAmount: toNum((r as any).mortgageAmount),
      shortfallRate: toNum((r as any).shortfallRate),
    }));
  }, [usageData]);

  const SORT_ACCESSOR: Record<SortKey, (r: UsageMetricsItem) => number> = {
    claimAmount: (r) => r.claimAmount,
    bidRate: (r) => r.bidRate,
    winPriceRate: (r) => r.winPriceRate,
    collateralValueRate: (r) => r.collateralValueRate,
    recoveryRate: (r) => r.recoveryRate,
    lossRate: (r) => r.lossRate,
  };

  const rows = useMemo(() => {
    const base = [...source];
    base.sort((a, b) => {
      const va = SORT_ACCESSOR[sortKey](a);
      const vb = SORT_ACCESSOR[sortKey](b);
      const diff = va - vb;
      return sortDir === 'asc' ? diff : -diff;
    });
    return base;
  }, [source, sortKey, sortDir]);

  const avg = useMemo(() => {
    const n = rows.length || 1;
    if (!rows.length) {
      return {
        claimAmount: 0,
        bidRate: 0,
        winPriceRate: 0,
        collateralValueRate: 0,
        recoveryRate: 0,
        lossRate: 0,
      };
    }
    const sum = rows.reduce(
      (a, c) => ({
        claimAmount: a.claimAmount + c.claimAmount,
        bidRate: a.bidRate + c.bidRate,
        winPriceRate: a.winPriceRate + c.winPriceRate,
        collateralValueRate: a.collateralValueRate + c.collateralValueRate,
        recoveryRate: a.recoveryRate + c.recoveryRate,
        lossRate: a.lossRate + c.lossRate,
      }),
      {
        claimAmount: 0,
        bidRate: 0,
        winPriceRate: 0,
        collateralValueRate: 0,
        recoveryRate: 0,
        lossRate: 0,
      },
    );
    return {
      claimAmount: Math.round(sum.claimAmount / n),
      bidRate: +(sum.bidRate / n).toFixed(1),
      winPriceRate: +(sum.winPriceRate / n).toFixed(1),
      collateralValueRate: +(sum.collateralValueRate / n).toFixed(1),
      recoveryRate: +(sum.recoveryRate / n).toFixed(1),
      lossRate: +(sum.lossRate / n).toFixed(1),
    };
  }, [rows]);

  const handleDownloadExcel = () => {
    const xlsxRows = rows.map((r) => ({
      용도: r.usage,
      청구금액: r.claimAmount,
      '낙찰률(%)': r.bidRate,
      '낙찰가율(%)': r.winPriceRate,
      '담보가치율(%)': r.collateralValueRate,
      '회수율(%)': r.recoveryRate,
      '손실률(%)': r.lossRate,
    }));
    downloadAsExcel(xlsxRows as any[], '용도별_NPL_지표');
  };

  const [selectedRow, setSelectedRow] = useState<UsageMetricsItem | null>(null);
  return (
    <s.Wrap>
      <s.Header>
        <s.HeaderRow>
          <s.Img $bg="#f0e8ff">
            <img src="/assets/images/icon/purple_apt.svg" alt="" />
          </s.Img>
          <s.ButtonDiv>
            <s.ExcelButton onClick={onToggleDetail} aria-label="상세 용도 전환">
              <img src="/assets/images/icon/rotation.svg" alt="" />
            </s.ExcelButton>
            <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
              <img src="/assets/images/icon/download-black.svg" alt="" />
            </s.ExcelButton>
          </s.ButtonDiv>
        </s.HeaderRow>
        <s.TitleBox>
          <h3>용도별 NPL 지표 현황</h3>
          <p>용도별 경매 대상 NPL 시장 현황 비교</p>
          <s.SelectedOptionBox>
            <s.SelectedOption>{periodSummary}</s.SelectedOption>
            <s.SelectedOption>
              {court && court !== 'all' ? court : `${region} 지역`}
            </s.SelectedOption>
            <s.SelectedOption>
              {fiMain === '전체'
                ? `${fiMain} 금융기관`
                : `${fiSub ? ` ${fiSub}` : ''}`}
            </s.SelectedOption>
          </s.SelectedOptionBox>
        </s.TitleBox>
      </s.Header>

      {loading ? (
        <LoaderWrap>
          <LoaderImg src="/assets/images/icon/loader.gif" alt="용도별" />
        </LoaderWrap>
      ) : (
        <TableContainer
          ref={ref}
          onMouseDown={onMouseDown}
          onMouseLeave={onMouseLeave}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
        >
          <s.Table style={{ width: '100%' }}>
            <thead>
              <tr>
                <Th align="left">용도</Th>
                <Th align="right">
                  <SortHeader onClick={() => toggleSort('claimAmount')}>
                    <Arrow $active={sortKey === 'claimAmount'} $dir={sortDir} />
                    청구금액
                  </SortHeader>
                </Th>
                <Th align="right">
                  <SortHeader onClick={() => toggleSort('bidRate')}>
                    <Arrow $active={sortKey === 'bidRate'} $dir={sortDir} />
                    낙찰률
                  </SortHeader>
                </Th>
                <Th align="right">
                  <SortHeader onClick={() => toggleSort('winPriceRate')}>
                    <Arrow
                      $active={sortKey === 'winPriceRate'}
                      $dir={sortDir}
                    />
                    낙찰가율
                  </SortHeader>
                </Th>
                <Th align="right">
                  <SortHeader onClick={() => toggleSort('collateralValueRate')}>
                    <Arrow
                      $active={sortKey === 'collateralValueRate'}
                      $dir={sortDir}
                    />
                    담보가치율
                  </SortHeader>
                </Th>
                <Th align="right">
                  <SortHeader onClick={() => toggleSort('recoveryRate')}>
                    <Arrow
                      $active={sortKey === 'recoveryRate'}
                      $dir={sortDir}
                    />
                    회수율
                  </SortHeader>
                </Th>
                <Th align="right">
                  <SortHeader onClick={() => toggleSort('lossRate')}>
                    <Arrow $active={sortKey === 'lossRate'} $dir={sortDir} />
                    손실률
                  </SortHeader>
                </Th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => (
                <Row
                  key={row.id}
                  $selected={row.id === selectedRow?.id}
                  onClick={() => setSelectedRow(row)}
                >
                  <s.Td align="left">{row.usage}</s.Td>
                  <s.Td align="right">{formatCurrency(row.claimAmount)}</s.Td>
                  <s.Td align="right">{row.bidRate}%</s.Td>
                  <s.Td align="right">{row.winPriceRate}%</s.Td>
                  <s.Td align="right">{row.collateralValueRate}%</s.Td>
                  <s.Td align="right">{row.recoveryRate}%</s.Td>
                  <s.Td align="right">{row.lossRate}%</s.Td>
                </Row>
              ))}

              <s.AvgTr>
                <s.TdAvg align="left">평균</s.TdAvg>
                <s.TdAvg align="right">
                  {formatCurrency(avg.claimAmount)}
                </s.TdAvg>
                <s.TdAvg align="right">{avg.bidRate}%</s.TdAvg>
                <s.TdAvg align="right">{avg.winPriceRate}%</s.TdAvg>
                <s.TdAvg align="right">{avg.collateralValueRate}%</s.TdAvg>
                <s.TdAvg align="right">{avg.recoveryRate}%</s.TdAvg>
                <s.TdAvg align="right">{avg.lossRate}%</s.TdAvg>
              </s.AvgTr>
            </tbody>
          </s.Table>
        </TableContainer>
      )}
      {selectedRow && (
        <BottomModal onClose={() => setSelectedRow(null)}>
          <TooltipHeader>
            <HeaderLeft>
              <BadgeLabel>{selectedRow.usage}</BadgeLabel>
              <TooltipTitle>{periodSummary}</TooltipTitle>
            </HeaderLeft>
            <CloseBtn
              onClick={() => setSelectedRow(null)}
              aria-label="툴팁 닫기"
            >
              ×
            </CloseBtn>
          </TooltipHeader>
          <TooltipList>
            <TipItem>
              <span>청구금액</span>
              <strong>{formatCurrency(selectedRow.claimAmount)}</strong>
            </TipItem>
            <TipItem>
              <span>근저당액</span>
              <strong>{formatCurrency(selectedRow.mortgageAmount)}</strong>
            </TipItem>
            <TipItem>
              <span>낙찰률</span>
              <strong>{`${selectedRow.bidRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>낙찰가율</span>
              <strong>{`${selectedRow.winPriceRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>담보가치율</span>
              <strong>{`${selectedRow.collateralValueRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>회수율</span>
              <strong>{`${selectedRow.recoveryRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>손실률</span>
              <strong>{`${selectedRow.lossRate}%`}</strong>
            </TipItem>
            <TipItem>
              <span>회수부족율</span>
              <strong>{`${selectedRow.shortfallRate}%`}</strong>
            </TipItem>
          </TooltipList>
          <ToolTipBottom>
            <ToolTipButton onClick={() => setSelectedRow(null)}>
              확인
            </ToolTipButton>
          </ToolTipBottom>
        </BottomModal>
      )}
    </s.Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 40px;
`;

const Th = styled.th<{ align?: 'left' | 'center' | 'right' }>`
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: -2%;
  angle: 0 deg;
  opacity: 1;
  gap: 4px;
  padding: 12px 14px 12px 12px;
  color: #424854;
  text-align: ${(p) => p.align ?? 'left'};
`;

const SortHeader = styled.button`
  font-weight: 500;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: -2%;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  cursor: pointer;
  background: transparent;
  border: 0;
  padding: 0;
`;

const Arrow = styled.img.attrs<{ $active?: boolean; $dir?: 'asc' | 'desc' }>(
  ({ $active, $dir }) => {
    if ($active) {
      return {
        src:
          $dir === 'desc'
            ? '/assets/images/arrow/blue_arrow_down.svg' //  활성 + 내림
            : '/assets/images/arrow/red_arrow_up.svg', //  활성 + 오름
        alt: '',
      };
    }
    return { src: '/assets/images/arrow/gray_arrow_down.svg', alt: '' }; // 비활성
  },
)<{ $active?: boolean; $dir?: 'asc' | 'desc' }>`
  width: 16px;
  height: 16px;
  transition:
    transform 120ms ease,
    opacity 120ms ease;
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 24px;
  height: 24px;
`;

const Row = styled.tr<{ $selected?: boolean }>`
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? '#E8E9ED' : 'transparent')};
  &:hover {
    background: ${({ $selected }) => ($selected ? '#E8E9ED' : '#F4F5F7')};
  }
  transition: background 120ms ease;
`;

const TooltipHeader = styled.div`
  display: flex;
  padding: 24px 20px 8px 20px;
  align-items: center;
  align-self: stretch;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
`;

const BadgeLabel = styled.span`
  background: #e8e9ed;
  color: #424854;
  font-weight: 700;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 6px;
`;

const TooltipTitle = styled.div`
  color: #424854;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 150% */
  letter-spacing: -0.32px;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  outline: none;

  font-size: 16px;
  color: #787d87;
  cursor: pointer;
  line-height: 1;
`;

const TooltipList = styled.div`
  display: flex;
  padding-top: 8px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const TipItem = styled.div`
  display: flex;
  padding: 4px 24px 12px 24px;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  align-self: stretch;

  span {
    font-size: 13px;
    color: #787d87;
  }
  strong {
    font-size: 14px;
    color: #424854;
  }
`;

const ToolTipBottom = styled.div`
  display: flex;
  padding: 16px 24px 24px 24px;
  align-items: center;
  gap: 8px;
`;

const ToolTipButton = styled.div`
  display: flex;
  height: 44px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  border-radius: 8px;
  background: var(--npl-semantic-background-solid-light-01, #f4f5f7);
  color: var(--npl-semantic-label-solid-light-secondary, #424854);

  /* npl/body/med/L_15 */
  font-family: 'Spoqa Han Sans Neo';
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px; /* 146.667% */
  letter-spacing: -0.3px;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  cursor: grab;

  &.grabbing {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
`;
